import { Component, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  BackendValidationError,
  ConfirmPassword,
  ErrorKeys,
  ErrorMessagesComponent,
  ErrorStateHighlightDirective,
  MarkFormTouchedDirective,
  PasswordInputComponent,
  PasswordService,
} from '@gb-webapp/gb-app';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize, map, Observable, of, tap } from 'rxjs';

interface ValidationRule {
  type: string;
  message: string;
}

interface FieldValidationRules {
  newPassword: ValidationRule[];
  confirmPassword: ValidationRule[];
}

@Component({
  selector: 'app-reset-password',
  imports: [
    ReactiveFormsModule,
    PasswordInputComponent,
    ErrorStateHighlightDirective,
    ErrorMessagesComponent,
    MarkFormTouchedDirective,
    TranslatePipe,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent {
  resetPasswordForm = new FormGroup({
    newPassword: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/^(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/),
      ],
    }),
    confirmPassword: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  isSubmitting = signal(false);

  serverValidationError = signal('');

  email = input.required<string>();

  otp = input.required<string>();

  public passwordChanged = output();

  // Centralized validation rules object
  fieldValidationRules: FieldValidationRules = {
    newPassword: [
      { type: 'required', message: 'authentication.password_required' },
      { type: 'minlength', message: 'Minimum 8 characters' },
      { type: 'pattern', message: 'Invalid Password' },
    ],
    confirmPassword: [
      { type: 'required', message: 'Please confirm your password' },
      { type: 'confirm', message: 'Passwords do not match' },
    ],
  };

  // Error mappings for app-error-messages component
  errorMappings = {
    confirmPassword: {
      [ErrorKeys.Confirm]: 'authentication.password_match_error',
      required: 'authentication.password_required',
    },
  };

  protected passwordService = inject(PasswordService);

  constructor() {
    this.resetPasswordForm.controls.confirmPassword.addValidators([
      ConfirmPassword(this.resetPasswordForm.controls.newPassword),
    ]);
  }

  handleResetPasswordSubmission(): Observable<boolean> {
    if (this.isSubmitting()) {
      return of(false);
    }

    this.isSubmitting.set(true);
    return this.passwordService
      .resetPassword(this.otp(), this.resetPasswordForm.controls.newPassword.value)
      .pipe(
        map(() => {
          this.passwordChanged.emit();
          return true;
        }),
        finalize(() => this.isSubmitting.set(false)),
        tap({
          error: (error) => {
            if (error.error.message) {
              this.resetPasswordForm.addValidators([
                BackendValidationError(ErrorKeys.ServerValidation),
              ]);
              this.serverValidationError.set(error.error.message);
              this.resetPasswordForm.updateValueAndValidity();
            }
          },
        })
      );
  }

  // Check if password meets requirements
  getPasswordValidationStatus(): { minLength: boolean; hasNumber: boolean; hasSpecial: boolean } {
    const passwordValue = this.resetPasswordForm.get('newPassword')?.value || '';
    return {
      minLength: passwordValue.length >= 8,
      hasNumber: /\d/.test(passwordValue),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue),
    };
  }

  // Helper method to get first error message from centralized validation rules
  getFieldErrorMessage(fieldName: keyof FieldValidationRules): string {
    const formControl = this.resetPasswordForm.get(fieldName);
    if (!formControl?.errors) return '';

    const fieldRules = this.fieldValidationRules[fieldName];
    if (!fieldRules) return '';

    for (const rule of fieldRules) {
      if (formControl.hasError(rule.type)) {
        return rule.message;
      }
    }
    return '';
  }
}
